import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, MessageCircle, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" } }),
};

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="contact-success" className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center max-w-lg px-6">
          <CheckCircle size={48} className="text-gold mx-auto mb-6" />
          <h2 className="font-heading text-3xl text-white mb-4" data-testid="contact-success-heading">Message Received</h2>
          <p className="text-white/60 font-body font-light text-sm leading-relaxed mb-8">
            Thank you for reaching out. Our team will respond within 24 hours. You can also reach us via WhatsApp for faster communication.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://wa.me/18764390976" target="_blank" rel="noopener noreferrer" data-testid="contact-success-whatsapp" className="inline-flex items-center justify-center gap-2 bg-gold text-black font-body font-medium text-sm px-8 py-3 hover:bg-gold-light transition-colors">
              <MessageCircle size={16} /> WhatsApp Us
            </a>
            <a href="/" data-testid="contact-success-home-link" className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-body font-light text-sm px-8 py-3 hover:border-white/40 transition-colors">Return Home</a>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className="pt-20">
      <section className="py-24 md:py-32 bg-luxury-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div initial="hidden" animate="visible" className="max-w-3xl mb-16">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-6">Contact</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-5xl sm:text-6xl font-light text-white leading-none mb-6">
              Let's Create Something<br /><span className="gold-gradient-text">Extraordinary</span>
            </motion.h1>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Form */}
            <motion.form initial="hidden" animate="visible" onSubmit={handleSubmit} data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <motion.div variants={fadeUp} custom={2}>
                  <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Full Name *</label>
                  <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm" />
                </motion.div>
                <motion.div variants={fadeUp} custom={3}>
                  <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Email *</label>
                  <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="your@email.com" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm" />
                </motion.div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <motion.div variants={fadeUp} custom={4}>
                  <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Phone</label>
                  <Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 (876) 000-0000" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm" />
                </motion.div>
                <motion.div variants={fadeUp} custom={5}>
                  <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Subject *</label>
                  <Input data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="How can we help?" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12 rounded-sm" />
                </motion.div>
              </div>
              <motion.div variants={fadeUp} custom={6} className="mb-8">
                <label className="text-white/60 text-xs font-body uppercase tracking-wider mb-2 block">Message *</label>
                <Textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your inquiry..." rows={5} className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-sm resize-none" />
              </motion.div>
              <motion.div variants={fadeUp} custom={7}>
                <button type="submit" disabled={loading} data-testid="contact-submit" className="inline-flex items-center gap-2 bg-gold text-black font-body font-medium text-sm px-10 py-4 hover:bg-gold-light transition-colors disabled:opacity-50">
                  {loading ? "Sending..." : "Send Message"} <Send size={16} />
                </button>
              </motion.div>
            </motion.form>

            {/* Contact Info */}
            <motion.div initial="hidden" animate="visible" className="space-y-8">
              <motion.div variants={fadeUp} custom={8} className="glass-card p-8">
                <Mail size={24} className="text-gold mb-4" />
                <h3 className="text-white font-heading text-xl mb-2">Email</h3>
                <a href="mailto:aristocratsja@gmail.com" className="text-white/60 font-body text-sm hover:text-gold transition-colors">aristocratsja@gmail.com</a>
              </motion.div>
              <motion.div variants={fadeUp} custom={9} className="glass-card p-8">
                <Phone size={24} className="text-gold mb-4" />
                <h3 className="text-white font-heading text-xl mb-2">Phone</h3>
                <a href="tel:+18764390976" className="text-white/60 font-body text-sm hover:text-gold transition-colors">+1 (876) 439-0976</a>
              </motion.div>
              <motion.div variants={fadeUp} custom={10} className="glass-card p-8">
                <MessageCircle size={24} className="text-gold mb-4" />
                <h3 className="text-white font-heading text-xl mb-2">WhatsApp</h3>
                <a href="https://wa.me/18764390976" target="_blank" rel="noopener noreferrer" className="text-white/60 font-body text-sm hover:text-gold transition-colors">Message us on WhatsApp</a>
              </motion.div>
              <motion.div variants={fadeUp} custom={11} className="glass-card p-8">
                <MapPin size={24} className="text-gold mb-4" />
                <h3 className="text-white font-heading text-xl mb-2">Location</h3>
                <p className="text-white/60 font-body text-sm">Kingston, Jamaica</p>
                <p className="text-white/40 font-body text-xs mt-1">Serving the Caribbean & Beyond</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
